/**
 * Maps extracted feature IDs to shift factor IDs.
 *
 * Resolution strategy:
 * 1) Explicit feature->factor mappings for known mismatches.
 * 2) Slug fallback: feat_tachycardia -> factor_tachycardia.
 */

const FEATURE_TO_FACTOR_MAP = {
  // Vitals
  feat_tachycardia: 'factor_tachycardia',
  feat_bradycardia: 'factor_bradycardia',
  feat_hypotension: 'factor_hypotension',
  feat_hypertension: 'factor_hypertension_severe',
  feat_hypoxia: 'factor_hypoxia',
  feat_fever: 'factor_fever_exam',
  feat_tachypnea: 'factor_tachypnea',

  // History
  feat_chest_pain_pleuritic: 'factor_pleuritic_pain',
  feat_chest_pain_exertional: 'factor_exertional_onset',
  feat_sudden_onset: 'factor_sudden_onset',
  feat_recent_surgery: 'factor_recent_surgery',
  feat_recent_travel: 'factor_recent_travel',
  feat_prior_dvt_pe: 'factor_prior_dvt_pe',
  feat_leg_swelling: 'factor_unilateral_leg_swelling',
  feat_calf_tenderness: 'factor_calf_tenderness',

  // Exam
  feat_diaphoresis: 'factor_diaphoresis',
  feat_jvd: 'factor_jvd',
  feat_new_murmur: 'factor_new_murmur',
  feat_peritoneal_signs: 'factor_peritoneal_signs',
  feat_focal_neuro_deficit: 'factor_focal_neuro_deficit',
  feat_meningismus: 'factor_meningismus',

  // Diagnostics
  feat_elevated_troponin: 'factor_elevated_troponin',
  feat_elevated_ddimer: 'factor_elevated_ddimer',
  feat_stemi_pattern: 'factor_ecg_stemi',
  feat_low_hemoglobin: 'factor_anemia',
  feat_leukocytosis: 'factor_leukocytosis',

  // Temporal
  feat_onset_sudden: 'factor_onset_sudden',
  feat_onset_hyperacute: 'factor_onset_hyperacute',
  feat_duration_hours: 'factor_duration_hours',
  feat_duration_days: 'factor_duration_days',
  feat_duration_chronic: 'factor_duration_months_plus',
  feat_trajectory_worsening: 'factor_trajectory_worsening',
  feat_thunderclap: 'factor_thunderclap_onset',
};

function slugToFactorId(featureId) {
  return String(featureId || '').replace(/^feat_/, 'factor_');
}

/**
 * @param {string[]} featureIds
 * @param {Set<string>} knownFactorIds
 * @returns {{ factorId: string, sourceFeatureId: string }[]}
 */
export function mapFeaturesToFactors(featureIds, knownFactorIds) {
  const results = [];
  const seen = new Set();
  for (const featureIdRaw of featureIds || []) {
    const featureId = String(featureIdRaw || '').trim();
    if (!featureId) continue;

    const explicit = FEATURE_TO_FACTOR_MAP[featureId];
    if (explicit && knownFactorIds?.has(explicit)) {
      const key = `${explicit}|${featureId}`;
      if (!seen.has(key)) {
        seen.add(key);
        results.push({ factorId: explicit, sourceFeatureId: featureId });
      }
      continue;
    }

    const slugged = slugToFactorId(featureId);
    if (knownFactorIds?.has(slugged)) {
      const key = `${slugged}|${featureId}`;
      if (!seen.has(key)) {
        seen.add(key);
        results.push({ factorId: slugged, sourceFeatureId: featureId });
      }
    }
  }
  return results;
}

/**
 * @param {import('./ddxTypes.js').TemporalExtractionResult} extraction
 * @param {Set<string>} knownFactorIds
 * @returns {string[]}
 */
export function mapTemporalContextToFactors(extraction, knownFactorIds) {
  const factorIds = [];
  const acuityMap = {
    hyperacute: 'factor_onset_hyperacute',
    acute: 'factor_onset_acute',
    subacute: 'factor_onset_subacute',
    chronic: 'factor_onset_chronic',
  };
  const acuityFactor = acuityMap[extraction?.acuityEstimate];
  if (acuityFactor && knownFactorIds?.has(acuityFactor)) factorIds.push(acuityFactor);

  const onsetMap = {
    sudden: 'factor_onset_sudden',
    gradual: 'factor_onset_acute',
    progressive: 'factor_trajectory_worsening',
    intermittent: 'factor_onset_intermittent',
  };
  const onsetFactor = onsetMap[extraction?.onsetPattern];
  if (onsetFactor && knownFactorIds?.has(onsetFactor)) factorIds.push(onsetFactor);

  const flagMap = {
    sudden_onset_pleuritic: 'factor_onset_sudden',
    thunderclap: 'factor_thunderclap_onset',
    tearing_onset: 'factor_tearing_onset',
    maximal_at_onset: 'factor_maximal_at_onset',
    syncope_at_onset: 'factor_syncope_at_onset',
  };
  for (const flag of extraction?.temporalRedFlags || []) {
    const mapped = flagMap[flag];
    if (mapped && knownFactorIds?.has(mapped)) factorIds.push(mapped);
  }

  return [...new Set(factorIds)];
}

export { FEATURE_TO_FACTOR_MAP };
